import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Logo from "@/components/Logo";
import Button from "@/components/Button";
import { Field, Select } from "@/components/Form";
import { Eyebrow, Headline } from "@/components/Editorial";
import ProgressBar from "@/components/ProgressBar";
import { Spinner } from "@/components/Loader";
import { toast } from "@/components/Toast";
import { affGet, affPost, setAffiliateToken } from "@/lib/affiliateApi";
import { useAffiliateAuth } from "@/lib/affiliateAuth";
import { C } from "@/lib/brand";

// Post-approval setup. Hit via emailed link: /affiliates/setup?token=<one-time>
// Multi-step: password → payout method → tax form. On completion, jumps to dashboard.
export default function AffiliateSetup() {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const { refreshProfile } = useAffiliateAuth();
  const setupToken = search.get("token");
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    payoutMethod: "",
    paypalEmail: "",
    country: "US",
    taxFormType: "W9",
    taxName: "",
    taxId: "",
    taxAddress: "",
    attestationSignature: "",
  });
  const update = (patch) => setData((p) => ({ ...p, ...patch }));
  // Stripe Connect status state — driven by the BE's
  // /payouts/stripe/status endpoint. Possible values:
  //   "idle"        — never started, default render
  //   "redirecting" — about to send to Stripe hosted onboarding
  //   "polling"     — back from Stripe, waiting for webhook to flip flags
  //   "ready"       — payoutReady === true; can advance to tax step
  //   "rejected"    — Stripe surfaced disabled_reason; ops needs to intervene
  const [stripeStatus, setStripeStatus] = useState("idle");
  const [stripeStatusDetail, setStripeStatusDetail] = useState(null);

  useEffect(() => {
    if (!setupToken) {
      toast("Invalid or missing setup link.", "error");
    }
  }, [setupToken]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step]);

  // Stripe return-from-onboarding handler. Query params come back as:
  //   ?stripe=return  — Stripe says onboarding is done. Poll status until
  //                     payoutReady flips, or the BE tells us it failed.
  //   ?stripe=refresh — link expired or user dropped out. Mint a fresh
  //                     onboarding URL and bounce them right back.
  const stripeParam = search.get("stripe");
  useEffect(() => {
    if (!stripeParam) return;
    // Jump to the payout step so the user lands on the right screen.
    setStep(2);
    update({ payoutMethod: "stripe" });
    if (stripeParam === "refresh") {
      // Re-mint the onboarding URL — silent reuse of the start endpoint.
      (async () => {
        try {
          setStripeStatus("redirecting");
          const res = await affPost("/affiliate/payouts/stripe/start", {}, { auth: true });
          if (res?.onboardingUrl) {
            window.location.href = res.onboardingUrl;
            return;
          }
          toast("Couldn't refresh your Stripe link. Try Continue again.", "error");
          setStripeStatus("idle");
        } catch (err) {
          toast(err?.detail || "Couldn't refresh your Stripe link.", "error");
          setStripeStatus("idle");
        }
      })();
      return;
    }
    if (stripeParam === "return") {
      setStripeStatus("polling");
      // First fetch is immediate; the useEffect below schedules retries.
      void pollStripeStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripeParam]);

  // Single status fetch. Returns the full BE status object so callers
  // can decide what to do with it; also commits the relevant flags to
  // local state so the screen renders correctly.
  const pollStripeStatus = async () => {
    try {
      const res = await affGet("/affiliate/payouts/stripe/status", { auth: true });
      setStripeStatusDetail(res);
      if (res?.payoutReady) {
        setStripeStatus("ready");
        return res;
      }
      if (res?.disabledReason) {
        setStripeStatus("rejected");
        return res;
      }
      // Still pending — keep polling.
      setStripeStatus("polling");
      return res;
    } catch (err) {
      // 404 or 5xx — surface a clean message but keep polling unless the
      // user navigates away. Avoid toasting on every retry.
      setStripeStatusDetail({ error: err?.detail || "Status check failed" });
      return null;
    }
  };

  // Backoff-ish polling: every 2s for the first 30s, then every 5s up to
  // a 3-minute ceiling. Stripe webhooks usually land in < 10s in test
  // mode but can lag in live mode if the connected account has manual
  // identity verification queued.
  useEffect(() => {
    if (stripeStatus !== "polling") return;
    let cancelled = false;
    let attempt = 0;
    const tick = async () => {
      if (cancelled) return;
      attempt += 1;
      const res = await pollStripeStatus();
      if (cancelled) return;
      if (res?.payoutReady || res?.disabledReason) return;
      const delay = attempt < 15 ? 2000 : 5000;
      if (attempt >= 36) {
        // ~3 minutes — stop polling and let the user retry manually.
        toast("Still waiting on Stripe. You can refresh this page to check again.", "info");
        return;
      }
      setTimeout(tick, delay);
    };
    setTimeout(tick, 2000);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripeStatus]);

  const total = 3;

  /* ----- Step 1: password ----- */
  const submitPassword = async () => {
    if (!data.password || data.password.length < 12) {
      toast("Password must be at least 12 characters.", "error");
      return;
    }
    if (data.password !== data.confirmPassword) {
      toast("Passwords don't match.", "error");
      return;
    }
    setSubmitting(true);
    try {
      const res = await affPost("/affiliate/setup/password", {
        token: setupToken,
        password: data.password,
      });
      if (res?.token) setAffiliateToken(res.token);
      if (res?.email) update({ email: res.email });
      await refreshProfile();
      setStep(2);
    } catch (err) {
      toast(formatSetupError(err, "set password"), "error");
    } finally {
      setSubmitting(false);
    }
  };

  /* ----- Step 2: payout ----- */
  const submitPayout = async () => {
    if (!data.payoutMethod) {
      toast("Pick a payout method.", "error");
      return;
    }
    // Stripe Connect path: ask the BE for a hosted onboarding URL and
    // redirect. The BE will receive the webhook on completion; user
    // returns to /affiliates/setup?stripe=return and the useEffect at
    // the top of this component picks up the flow from there.
    if (data.payoutMethod === "stripe") {
      // If we're already onboarded (post-return polling completed) just
      // advance to the tax step.
      if (stripeStatus === "ready") {
        setStep(3);
        return;
      }
      setSubmitting(true);
      try {
        setStripeStatus("redirecting");
        const res = await affPost("/affiliate/payouts/stripe/start", {}, { auth: true });
        if (res?.onboardingUrl) {
          window.location.href = res.onboardingUrl;
          return;
        }
        toast("Couldn't generate Stripe onboarding link. Try again in a moment.", "error");
        setStripeStatus("idle");
      } catch (err) {
        toast(formatSetupError(err, "start Stripe onboarding"), "error");
        setStripeStatus("idle");
      } finally {
        setSubmitting(false);
      }
      return;
    }
    setSubmitting(true);
    try {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.paypalEmail)) {
        toast("Enter a valid PayPal email.", "error");
        setSubmitting(false);
        return;
      }
      await affPost(
        "/affiliate/payouts/setup-paypal",
        { paypalEmail: data.paypalEmail },
        { auth: true },
      );
      setStep(3);
    } catch (err) {
      toast(formatSetupError(err, "save payout method"), "error");
    } finally {
      setSubmitting(false);
    }
  };

  /* ----- Step 3: tax ----- */
  const submitTax = async () => {
    if (!data.taxName || !data.taxId || !data.taxAddress) {
      toast("Please complete the tax form.", "error");
      return;
    }
    const sig = (data.attestationSignature || "").trim();
    if (sig.length < 2 || sig.length > 200) {
      toast("Please type your full legal name in the signature field (2–200 characters).", "error");
      return;
    }
    setSubmitting(true);
    try {
      await affPost(
        "/affiliate/tax-form",
        {
          country: data.country,
          formType: data.taxFormType,
          fullName: data.taxName,
          taxId: data.taxId,
          address: data.taxAddress,
          attestationSignature: sig,
        },
        { auth: true },
      );
      await refreshProfile();
      navigate("/affiliates/dashboard", { replace: true });
    } catch (err) {
      toast(formatSetupError(err, "save tax info"), "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="affiliate-setup-page" style={{ minHeight: "100vh", background: C.ink, color: C.cream }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 24px" }}>
        <Link to="/affiliates"><Logo light size={22} /></Link>
      </header>

      <ProgressBar current={step} total={total} />

      <div style={{ maxWidth: 560, margin: "0 auto", padding: "48px 24px 120px" }}>
        {step === 1 && (
          <SetupStep stepNumber={1} title="SET YOUR" italic="password." subtitle={data.email ? `Email: ${data.email}` : "Use the link in your approval email."}>
            <Field label="PASSWORD" type="password" name="new-password" id="setup-password" autoComplete="new-password" value={data.password} onChange={(v) => update({ password: v })} placeholder="At least 12 characters" testId="setup-password" required autoFocus />
            <Field label="CONFIRM PASSWORD" type="password" name="new-password" id="setup-confirm-password" autoComplete="new-password" value={data.confirmPassword} onChange={(v) => update({ confirmPassword: v })} placeholder="Re-enter password" testId="setup-confirm-password" required />
            <div style={{ marginTop: 24 }}>
              <Button variant="peach" size="lg" block testId="setup-password-btn" onClick={submitPassword} disabled={submitting || !setupToken}>
                {submitting ? <Spinner /> : "CONTINUE →"}
              </Button>
            </div>
          </SetupStep>
        )}

        {step === 2 && (
          <SetupStep stepNumber={2} title="PAYOUT" italic="method." subtitle="How would you like to receive your monthly commission?">
            <PayoutOption
              value="stripe"
              active={data.payoutMethod === "stripe"}
              onClick={() => update({ payoutMethod: "stripe" })}
              title="Stripe Connect"
              detail="US bank account · 1–2 day deposits"
              testId="setup-payout-stripe"
              badge={stripeStatus === "ready" ? "READY ✓" : null}
            />
            <PayoutOption
              value="paypal"
              active={data.payoutMethod === "paypal"}
              onClick={() => update({ payoutMethod: "paypal" })}
              title="PayPal"
              detail="US and international · 3–5 day deposits"
              testId="setup-payout-paypal"
            />
            {data.payoutMethod === "stripe" && (
              <StripeStatusPanel
                status={stripeStatus}
                detail={stripeStatusDetail}
                onRecheck={() => {
                  setStripeStatus("polling");
                  void pollStripeStatus();
                }}
              />
            )}
            {data.payoutMethod === "paypal" && (
              <div style={{ background: C.cream, color: C.ink, padding: 22, marginTop: 14 }}>
                <Field
                  label="PAYPAL EMAIL"
                  type="email"
                  value={data.paypalEmail}
                  onChange={(v) => update({ paypalEmail: v })}
                  placeholder="paypal-account@example.com"
                  testId="setup-paypal-email"
                  required
                />
              </div>
            )}
            <div style={{ marginTop: 24, display: "flex", gap: 12 }}>
              <Button variant="ghost" testId="setup-payout-back" onClick={() => setStep(1)}>← BACK</Button>
              <Button
                variant="peach"
                size="lg"
                testId="setup-payout-btn"
                onClick={submitPayout}
                disabled={
                  submitting
                  || stripeStatus === "redirecting"
                  || stripeStatus === "polling"
                  || (data.payoutMethod === "stripe" && stripeStatus === "rejected")
                }
              >
                {submitting || stripeStatus === "redirecting" ? <Spinner /> : payoutCtaLabel(data.payoutMethod, stripeStatus)}
              </Button>
            </div>
          </SetupStep>
        )}

        {step === 3 && (
          <SetupStep stepNumber={3} title="TAX" italic="information." subtitle="Required by US law for any affiliate earning over $600/year.">
            <div style={{ background: C.cream, color: C.ink, padding: 22 }}>
              <Select
                label="COUNTRY"
                value={data.country}
                onChange={(v) => update({ country: v, taxFormType: v === "US" ? "W9" : "W8BEN" })}
                options={[
                  { value: "US", label: "United States — submit W9" },
                  { value: "INT", label: "International — submit W8BEN" },
                ]}
                testId="setup-tax-country"
              />
              <Field
                label="FULL LEGAL NAME"
                value={data.taxName}
                onChange={(v) => update({ taxName: v })}
                placeholder="As shown on your tax forms"
                testId="setup-tax-name"
                autoComplete="off"
                name="vy9-legal-name"
                required
              />
              <TaxIdField
                label={data.taxFormType === "W9" ? "SSN OR EIN" : "FOREIGN TAX ID"}
                value={data.taxId}
                onChange={(v) => update({ taxId: v })}
                placeholder={data.taxFormType === "W9" ? "XXX-XX-XXXX or XX-XXXXXXX" : "Tax identification number"}
              />
              <Field
                label="ADDRESS"
                value={data.taxAddress}
                onChange={(v) => update({ taxAddress: v })}
                placeholder="Street, city, state/province, postal code"
                testId="setup-tax-address"
                autoComplete="off"
                name="vy9-tax-address"
                required
              />
            </div>
            <AttestationBlock
              formType={data.taxFormType}
              fullName={data.taxName}
              value={data.attestationSignature}
              onChange={(v) => update({ attestationSignature: v })}
            />
            <div style={{ marginTop: 24, display: "flex", gap: 12 }}>
              <Button variant="ghost" testId="setup-tax-back" onClick={() => setStep(2)}>← BACK</Button>
              <Button variant="peach" size="lg" testId="setup-tax-btn" onClick={submitTax} disabled={submitting}>
                {submitting ? <Spinner /> : "ENTER DASHBOARD →"}
              </Button>
            </div>
          </SetupStep>
        )}
      </div>
    </div>
  );
}

function SetupStep({ title, italic, subtitle, children, stepNumber }) {
  return (
    <div>
      <Eyebrow color={C.peach}>★ STEP {String(stepNumber).padStart(2, "0")} / 03</Eyebrow>
      <Headline before={title} italic={italic} color={C.cream} italicColor={C.peach} size={56} className="mt-6" />
      {subtitle && (
        <p style={{ marginTop: 16, fontSize: 14, color: "rgba(250,246,240,0.7)", lineHeight: 1.6 }}>{subtitle}</p>
      )}
      <div style={{ marginTop: 28 }}>{children}</div>
    </div>
  );
}

// W9 / W8BEN attestation block. The BE requires
// `attestationSignature` (2–200 chars) — typing the signer's full legal
// name acts as the e-signature, exactly like Stripe/Gusto/Deel handle
// the W9 sign step. We surface the canonical IRS perjury statement above
// the input so the signature is legally meaningful, plus a "use my
// legal name" shortcut to reduce friction. Mismatch between signature
// and legal name above is allowed (different cultures/scripts), so we
// don't hard-enforce equality — just nudge.
function AttestationBlock({ formType, fullName, value, onChange }) {
  const isW9 = formType === "W9";
  const attestation = isW9
    ? "Under penalties of perjury, I certify that: (1) the number shown above is my correct taxpayer identification number, (2) I am not subject to backup withholding, and (3) I am a U.S. person."
    : "Under penalties of perjury, I declare that I have examined the information on this form and to the best of my knowledge it is true, correct, and complete. I certify I am the beneficial owner of the income for which this form relates and am not a U.S. person.";
  const today = new Date().toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  return (
    <div
      data-testid="setup-tax-attestation"
      style={{
        background: "rgba(244,201,178,0.06)",
        border: `1px solid rgba(244,201,178,0.18)`,
        color: C.cream,
        padding: 22,
        marginTop: 18,
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.22em", color: C.peach, marginBottom: 10 }}>
        ★ SIGN — {isW9 ? "W9 ATTESTATION" : "W8BEN ATTESTATION"}
      </div>
      <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: "rgba(250,246,240,0.78)" }}>
        {attestation}
      </p>
      <p
        style={{
          marginTop: 12,
          marginBottom: 16,
          fontSize: 11,
          color: "rgba(250,246,240,0.55)",
          fontFamily: '"Instrument Serif", Georgia, serif',
          fontStyle: "italic",
          lineHeight: 1.5,
        }}
      >
        Typing your full legal name below acts as your electronic signature, equivalent to signing on paper.
      </p>
      <label
        style={{
          display: "block",
          fontSize: 11,
          fontWeight: 800,
          letterSpacing: "0.2em",
          color: "rgba(250,246,240,0.7)",
          marginBottom: 8,
        }}
      >
        SIGNATURE (TYPE FULL LEGAL NAME) <span style={{ color: C.coral }}>*</span>
      </label>
      <div style={{ display: "flex", gap: 8, alignItems: "stretch", flexWrap: "wrap" }}>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={fullName || "Your full legal name"}
          data-testid="setup-tax-signature"
          autoComplete="off"
          maxLength={200}
          style={{
            flex: 1,
            minWidth: 220,
            padding: "14px 16px",
            background: C.pure,
            border: `1.5px solid rgba(250,246,240,0.18)`,
            fontSize: 18,
            color: C.ink,
            fontFamily: '"Instrument Serif", Georgia, serif',
            fontStyle: "italic",
            outline: "none",
            boxSizing: "border-box",
          }}
        />
        {fullName && fullName.trim().length >= 2 && value !== fullName && (
          <button
            type="button"
            onClick={() => onChange(fullName)}
            data-testid="setup-tax-signature-autofill"
            style={{
              padding: "0 14px",
              background: "transparent",
              border: `1px dashed rgba(244,201,178,0.4)`,
              color: C.peach,
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: "0.22em",
              cursor: "pointer",
            }}
          >
            USE MY LEGAL NAME
          </button>
        )}
      </div>
      <div
        style={{
          marginTop: 10,
          fontSize: 11,
          color: "rgba(250,246,240,0.5)",
          letterSpacing: "0.02em",
        }}
      >
        Signed on {today}
      </div>
    </div>
  );
}

// Tax-ID input with built-in obfuscation. Treats SSN/EIN/foreign tax
// IDs like passwords visually — but uses `type="text"` with CSS
// `-webkit-text-security: disc` masking instead of `type="password"`.
// Reason: password managers (1Password, Proton Pass, Bitwarden) hook
// on type="password" and aggressively auto-fill saved credentials,
// which is exactly what we *don't* want — we saw real users land here
// with a saved voya9.com login password injected into their SSN field.
// CSS masking gives identical visual privacy without the auto-fill
// hooks. Falls back gracefully on Firefox (no -webkit-text-security
// support) where we render an actual password input as the masked
// state, since Firefox doesn't aggressively fill non-password-page
// password fields anyway.
function TaxIdField({ label, value, onChange, placeholder }) {
  const [show, setShow] = useState(false);
  // Randomized name attribute — Chrome's heuristic password manager
  // saves anything labelled "tax" or "ssn" if the name is stable, even
  // when autoComplete="off" is set. A nonce-style name avoids that.
  const nameRef = useMemo(
    () => `vy9-taxid-${Math.random().toString(36).slice(2, 9)}`,
    [],
  );
  return (
    <div style={{ marginTop: 14 }}>
      <label
        style={{
          display: "block",
          fontSize: 11,
          fontWeight: 800,
          letterSpacing: "0.2em",
          color: C.warmGray,
          marginBottom: 8,
        }}
      >
        {label} <span style={{ color: C.coral }}>*</span>
      </label>
      <div style={{ position: "relative" }}>
        <input
          type="text"
          name={nameRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          inputMode="numeric"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          data-testid="setup-tax-id"
          data-lpignore="true"
          data-1p-ignore="true"
          data-protonpass-ignore="true"
          data-form-type="other"
          style={{
            width: "100%",
            padding: "14px 64px 14px 16px",
            background: C.pure,
            border: `1.5px solid ${C.warmGray3}`,
            fontSize: 15,
            color: C.ink,
            fontFamily: show ? "JetBrains Mono, monospace" : "system-ui, sans-serif",
            letterSpacing: show ? "0.05em" : "normal",
            outline: "none",
            boxSizing: "border-box",
            // CSS text masking that doesn't trigger password manager
            // auto-fill heuristics. -webkit-text-security covers
            // Chrome/Safari/Edge; text-security is the W3C draft prop
            // for forward-compat.
            WebkitTextSecurity: show ? "none" : "disc",
            textSecurity: show ? "none" : "disc",
          }}
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          data-testid="setup-tax-id-toggle"
          aria-label={show ? "Hide tax ID" : "Show tax ID"}
          style={{
            position: "absolute",
            top: "50%",
            right: 10,
            transform: "translateY(-50%)",
            background: "transparent",
            border: 0,
            color: C.peachDeep,
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: "0.2em",
            cursor: "pointer",
            padding: "6px 8px",
          }}
        >
          {show ? "HIDE" : "SHOW"}
        </button>
      </div>
      <div
        style={{
          marginTop: 6,
          fontSize: 11,
          color: C.warmGray,
          fontFamily: '"Instrument Serif", Georgia, serif',
          fontStyle: "italic",
          lineHeight: 1.45,
        }}
      >
        Encrypted in transit and at rest. Required by US tax law to report earnings over $600/year.
      </div>
    </div>
  );
}

function PayoutOption({ active, onClick, title, detail, testId, badge }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      style={{
        width: "100%",
        padding: "18px 22px",
        background: active ? C.peach : "rgba(250,246,240,0.06)",
        color: active ? C.ink : C.cream,
        border: `1.5px solid ${active ? C.peach : "rgba(250,246,240,0.2)"}`,
        textAlign: "left",
        cursor: "pointer",
        marginBottom: 10,
        display: "flex",
        flexDirection: "column",
        gap: 4,
        position: "relative",
      }}
    >
      <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: 16, fontWeight: 800, letterSpacing: "-0.01em" }}>{title}</span>
        {badge && (
          <span
            style={{
              fontSize: 9,
              fontWeight: 800,
              letterSpacing: "0.22em",
              padding: "3px 8px",
              background: active ? C.ink : C.peach,
              color: active ? C.peach : C.ink,
            }}
          >
            {badge}
          </span>
        )}
      </span>
      <span style={{ fontSize: 12, opacity: 0.75 }}>{detail}</span>
    </button>
  );
}

// Continue-button label tracks both the chosen method and the Stripe
// onboarding lifecycle. We don't want a generic "CONTINUE →" mid-poll —
// it makes users mash the button.
function payoutCtaLabel(method, stripeStatus) {
  if (method === "stripe") {
    if (stripeStatus === "ready") return "CONTINUE →";
    if (stripeStatus === "polling") return "VERIFYING WITH STRIPE…";
    if (stripeStatus === "rejected") return "RESOLVE WITH STRIPE";
    if (stripeStatus === "redirecting") return "REDIRECTING…";
    return "CONNECT WITH STRIPE →";
  }
  return "CONTINUE →";
}

// Status surface for the Stripe Connect path. Renders different copy
// for each lifecycle state — explicit so members always know what's
// happening and what to do next.
function StripeStatusPanel({ status, detail, onRecheck }) {
  const base = {
    padding: 18,
    marginTop: 14,
    fontSize: 13,
    lineHeight: 1.55,
    color: C.cream,
  };
  if (status === "ready") {
    return (
      <div
        data-testid="stripe-status-ready"
        style={{
          ...base,
          background: "rgba(166,196,165,0.12)",
          border: `1px solid ${C.sageDeep}66`,
        }}
      >
        <strong style={{ color: C.sageDeep, fontSize: 11, fontWeight: 800, letterSpacing: "0.22em", display: "block", marginBottom: 6 }}>
          ★ STRIPE READY
        </strong>
        Your Stripe Connect account is verified. Approved commissions will be deposited to your linked bank account, usually within 1–2 business days of release. Continue to finish setup.
      </div>
    );
  }
  if (status === "polling") {
    return (
      <div
        data-testid="stripe-status-polling"
        style={{
          ...base,
          background: "rgba(244,201,178,0.08)",
          border: `1px solid rgba(244,201,178,0.2)`,
        }}
      >
        <strong style={{ color: C.peach, fontSize: 11, fontWeight: 800, letterSpacing: "0.22em", display: "block", marginBottom: 6 }}>
          ★ VERIFYING WITH STRIPE…
        </strong>
        We're confirming your Stripe Connect account is ready. This usually takes a few seconds — leave this page open.
        <button
          type="button"
          onClick={onRecheck}
          data-testid="stripe-status-recheck"
          style={{
            marginTop: 12,
            background: "transparent",
            border: 0,
            color: C.peach,
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: "0.22em",
            cursor: "pointer",
            padding: 0,
          }}
        >
          CHECK AGAIN ↻
        </button>
      </div>
    );
  }
  if (status === "rejected") {
    return (
      <div
        data-testid="stripe-status-rejected"
        style={{
          ...base,
          background: "rgba(232,107,82,0.1)",
          border: `1px solid ${C.coral}66`,
        }}
      >
        <strong style={{ color: C.coral, fontSize: 11, fontWeight: 800, letterSpacing: "0.22em", display: "block", marginBottom: 6 }}>
          ● STRIPE NEEDS MORE INFO
        </strong>
        Stripe couldn't finalize your account.{detail?.disabledReason ? ` Reason: ${detail.disabledReason}.` : ""} Switch to PayPal to keep moving, or contact support to resolve.
      </div>
    );
  }
  if (status === "redirecting") {
    return (
      <div data-testid="stripe-status-redirecting" style={{ ...base, background: "rgba(250,246,240,0.04)" }}>
        Redirecting you to Stripe…
      </div>
    );
  }
  // idle
  return (
    <div
      data-testid="stripe-status-idle"
      style={{
        ...base,
        background: "rgba(250,246,240,0.04)",
        border: `1px solid rgba(250,246,240,0.12)`,
      }}
    >
      You'll be sent to Stripe to verify your identity and connect your bank account. It usually takes 2–3 minutes. We'll bring you back here automatically when you're done.
    </div>
  );
}


// Turn an `affPost` error into a member-readable toast. Handles all
// three common shapes: AffiliateApiError with FastAPI validation
// `detail` arrays (422), AffiliateApiError with a plain string `detail`
// (400/401/403/404), and unknown/network errors. We also detect the
// "endpoint missing" 404 case and surface a redeploy hint so the user
// knows the FE/BE are out of sync rather than thinking their link is
// broken.
function formatSetupError(err, verb = "save") {
  if (!err) return `Could not ${verb}. Please try again.`;
  // BE shipped the route but our server hasn't redeployed yet —
  // distinguish from a real "Not Found" by checking the detail string.
  if (err.status === 404 && /not found/i.test(String(err.detail || ""))) {
    return `This setup endpoint isn't live on this environment yet. If you're on production, ask the team to redeploy; otherwise switch to preview.`;
  }
  const detail = err.detail;
  if (Array.isArray(detail)) {
    const first = detail[0] || {};
    const field = Array.isArray(first.loc) ? first.loc.filter((x) => x !== "body").join(".") : null;
    const msg = first.msg || first.message || "Invalid value";
    return field ? `${field}: ${msg}` : msg;
  }
  if (typeof detail === "string" && detail.trim()) return detail;
  if (err.message && typeof err.message === "string") return err.message;
  return `Could not ${verb}. Please use the link from your approval email.`;
}
